import { Component, HostListener, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from '../../../data-access/notification.service';
import {
	NotificationMessageSummary,
	NotificationMessageFilter,
	NotificationStatus,
	NOTIFICATION_STATUSES
} from '../../../domain/types/notification';
import { NotificationMessageModalComponent } from '../notification-message-modal/notification-message-modal.component';
import { NotificationRequeueModalComponent } from '../notification-requeue-modal/notification-requeue-modal.component';

@Component({
	selector: 'notification-log',
	templateUrl: './notification-log.component.html',
	styleUrls: ['./notification-log.component.scss']
})
export class NotificationLogComponent implements OnInit {
	readonly statuses = NOTIFICATION_STATUSES;

	// Filter inputs (bound to the form controls).
	filterStatus: NotificationStatus | '' = '';
	filterRecipient = '';
	filterCaseId: number | null = null;
	filterFrom = '';
	filterTo = '';

	messages: NotificationMessageSummary[] = [];
	loading = false;
	loadingMore = false;
	/** True once a page returns fewer than pageSize rows — nothing more to fetch. */
	reachedEnd = false;
	error = '';

	/** Page size — roughly a screenful plus headroom so the first load is small and
	 *  the rest streams in on scroll (keyset-paginated by (createdAt, id)), rather
	 *  than pulling hundreds of rows up front. */
	private readonly pageSize = 50;

	/** Bumped on every load(); a loadMore() (or older load()) whose request resolves
	 *  after a newer load() discards its result, so a filter change can't leave
	 *  stale-filter rows appended or a stale keyset cursor in play. */
	private generation = 0;

	constructor(
		private readonly svc: NotificationService,
		private readonly dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.load();
	}

	/** Build the active filter; with a cursor it requests the next page. */
	private buildFilter(cursor?: NotificationMessageSummary): NotificationMessageFilter {
		return {
			status: this.filterStatus || undefined,
			recipient: this.filterRecipient.trim() || undefined,
			caseId: this.filterCaseId ?? undefined,
			from: this.filterFrom || undefined,
			to: this.filterTo || undefined,
			limit: this.pageSize,
			beforeCreatedAt: cursor?.createdAt,
			beforeId: cursor?.id
		};
	}

	/** Reset and load the first page (called on filter change). */
	async load(): Promise<void> {
		const gen = ++this.generation;
		this.loading = true;
		this.error = '';
		this.reachedEnd = false;
		try {
			const page = await this.svc.getMessages(this.buildFilter());
			if (gen !== this.generation) return;   // superseded by a newer load()
			this.messages = page;
			this.reachedEnd = page.length < this.pageSize;
		} catch {
			if (gen !== this.generation) return;
			this.error = 'Failed to load notifications.';
			this.messages = [];
		} finally {
			if (gen === this.generation) this.loading = false;
		}
	}

	/** Append the next page using the last row as the keyset cursor. */
	async loadMore(): Promise<void> {
		if (this.loading || this.loadingMore || this.reachedEnd || this.messages.length === 0) return;
		const gen = this.generation;
		this.loadingMore = true;
		try {
			const cursor = this.messages[this.messages.length - 1];
			const page = await this.svc.getMessages(this.buildFilter(cursor));
			if (gen !== this.generation) return;   // a filter-change load() superseded this page
			this.messages = this.messages.concat(page);
			if (page.length < this.pageSize) this.reachedEnd = true;
		} catch {
			if (gen === this.generation) this.error = 'Failed to load more notifications.';
		} finally {
			this.loadingMore = false;
		}
	}

	@HostListener('window:scroll')
	onScroll(): void {
		const el = document.scrollingElement || document.documentElement;
		// Within ~600px of the bottom — fetch the next page before the user hits the end.
		if (el.scrollHeight - el.scrollTop - el.clientHeight < 600) {
			this.loadMore();
		}
	}

	clearFilters(): void {
		this.filterStatus = '';
		this.filterRecipient = '';
		this.filterCaseId = null;
		this.filterFrom = '';
		this.filterTo = '';
		this.load();
	}

	statusBadgeClass(status: NotificationStatus): string {
		switch (status) {
			case 'Sent':    return 'badge-success';
			case 'Failed':  return 'badge-danger';
			case 'Bounced': return 'badge-warning';
			case 'Held':    return 'badge-secondary';
			default:        return 'badge-info';   // Pending
		}
	}

	canRequeue(m: NotificationMessageSummary): boolean {
		return m.status === 'Held' || m.status === 'Failed' || m.status === 'Bounced';
	}

	async viewBody(m: NotificationMessageSummary): Promise<void> {
		try {
			const detail = await this.svc.getMessage(m.id);
			NotificationMessageModalComponent.open(this.dialog, detail);
		} catch {
			this.error = 'Failed to load the message body.';
		}
	}

	async requeue(m: NotificationMessageSummary): Promise<void> {
		// Pre-fill the redirect field with the admin's own email (resolved
		// server-side) so "Redirect to…" defaults to themselves; they can still
		// type a different address. A failed lookup just leaves the field blank.
		const ownEmail = await this.svc.getMyEmail().catch(() => '');
		const req = await firstValueFrom(
			NotificationRequeueModalComponent.open(this.dialog, m, ownEmail).afterClosed()
		);
		if (!req) return;   // cancelled
		try {
			await this.svc.requeueMessage(m.id, req);
			await this.load();
		} catch {
			this.error = 'Failed to requeue the message.';
		}
	}
}
